// generated

import flattenHierarchy from "backend/flattenHierarchy";
import useElement from "backend/hooks/elements/useElement";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import useScorecardTopElement from "backend/hooks/model/useScorecardTopElement";
import StrategyToolbar from "components/toolbars/StrategyToolbar";
import { ElementOverviewDonutCard } from "design/components/cards/ElementOverviewDonutCard";
import OverallPerformanceCard from "design/components/cards/OverallPerformanceCard";
import { IChartSelectEvent } from "design/components/charts/types";
import { PerformanceSummaryBars } from "design/components/lists/SummaryCard";
import InitiativeDrillDown from "design/components/popups/InitiativeDrillDown";
import KPIDrillDown from "design/components/popups/KPIDrillDown";
import OverviewScorecardTable from "design/components/tables/OverviewScorecardTable";
import Center from "design/layouts/Center";
import PageContainer from "design/layouts/PageContainer";
import SplitView from "design/layouts/SplitView";
import Page from "design/pages/Page";
import { t } from "i18next";
import * as conf from "qpr-base/configuration";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import Loader from "qpr-base/ui/components/Loader";
import React from "react";
import { Switch } from "@material-ui/core";
import DonutLargeIcon from "@material-ui/icons/DonutLarge";
import TableChartIcon from "@material-ui/icons/TableChart";

const Overview = (props: {
  elementId?: string;
  setId?: string;
  backgroundColor?: string;
  isComponent?: boolean;
  hideRanges?: boolean;
}) => {
  const { data: root } = useScorecardTopElement("L0");
  const ctx = useAppContext();

  const [showKPIModal, setShowKPIModal] =
    React.useState<IChartSelectEvent | null>(null);
  const [showInitiatives, setShowInitiatives] =
    React.useState<IChartSelectEvent | null>(null);
  const [scorecardDesignEnabled, setScorecardDesignEnabled] =
    React.useState(false);

  const {
    data,
    isLoading: isDataLoading,
    isPreviousData,
    isRefetching,
  } = useHierarchy(ctx.elementId || root?.id);

  const overviewKPIlist = React.useMemo(
    () =>
      flattenHierarchy(data).filter(
        (el) => el.typename === "KPI" && el.STATUS === "1"
      ),
    [data]
  );

  const overviewInitiativelist = React.useMemo(
    () =>
      flattenHierarchy(data).filter(
        (el) => el.typename === "Initiative" && el.STATUS === "1"
      ),
    [data]
  );

  const overviewScorecardElements = React.useMemo(() => flattenHierarchy(data), [data]);

  const { data: el, isLoading: isElementLoading } = useElement(
    ctx.elementId || root?.id
  );

  if (!data || isElementLoading || isDataLoading || isRefetching || isPreviousData) {
    return (
      <div>
        <StrategyToolbar />
        <Loader />
      </div>
    );
  }

  return (
    <Page
      isLoading={!data || isElementLoading || isDataLoading || isRefetching || isPreviousData}
      Toolbar={StrategyToolbar}
    >
      <PageContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              background: "#eef2ff",
              border: "1px solid #c7d2fe",
              borderRadius: "999px",
              padding: "4px 10px",
              color: "#1e3a8a",
              fontWeight: 600,
              fontSize: "0.86rem",
              width: "fit-content",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                opacity: scorecardDesignEnabled ? 0.65 : 1,
              }}
            >
              <DonutLargeIcon style={{ fontSize: "0.95rem" }} />
              {t("overview.Overview")}
            </span>
            <Switch
              color="primary"
              size="small"
              checked={scorecardDesignEnabled}
              onChange={(e) => setScorecardDesignEnabled(e.target.checked)}
            />
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                opacity: scorecardDesignEnabled ? 1 : 0.65,
              }}
            >
              <TableChartIcon style={{ fontSize: "0.95rem" }} />
              {t("overview.Table")}
            </span>
          </div>
        </div>

        {scorecardDesignEnabled ? (
          <OverviewScorecardTable
            elements={overviewScorecardElements}
            language={ctx.language}
            corporateScore={
              el?.SCORE_YTD_valuewithoutsymbol ||
              el?.SCORE_prettyvalue ||
              "--"
            }
          />
        ) : null}

        {!scorecardDesignEnabled ? (
          <>
            <KPIDrillDown
              title={
                showKPIModal?.name === "No Value"
                  ? t("overview.KPIs with no data")
                  : `${showKPIModal?.name} ${t("overview.performing KPIs")}`
              }
              showModal={showKPIModal}
              onClose={() => {
                setShowKPIModal(null);
              }}
              items={overviewKPIlist}
            />
            <InitiativeDrillDown
              title={
                showInitiatives?.name === "No Value"
                  ? t("overview.Initiatives with no data")
                  : `${showInitiatives?.name} ${t("overview.performing Initiatives")}`
              }
              showModal={showInitiatives}
              onClose={() => {
                setShowInitiatives(null);
              }}
            />

            <SplitView partCnt={1} gap="30px">
              <Center>
                {conf.getOverviewDashboardSettings().hideOverallPerformance === true ? null : (
                  <div style={{ height: "100%" }}>
                    <OverallPerformanceCard
                      element={el!}
                      periodName={el?.periodname || ""}
                      key="PERF_W_YTD"
                      titleKey="PERF_W_YTD_prettyvalue"
                      title={t("overview.Overall Performance")}
                    />
                  </div>
                )}
                <ElementOverviewDonutCard
                  title={t("overview.KPI Overview")}
                  data={overviewKPIlist}
                  typenames={["KPI"]}
                  periodName={el?.periodname || ""}
                  onSelect={(event) => {
                    setShowKPIModal(event);
                  }}
                />
                <ElementOverviewDonutCard
                  title={t("overview.Initiative Overview")}
                  data={overviewInitiativelist}
                  typenames={["Initiative"]}
                  periodName={el?.periodname || ""}
                  onSelect={(event) => {
                    setShowInitiatives(event);
                  }}
                />
              </Center>
            </SplitView>

            <PerformanceSummaryBars
              elements={data}
              periodName={ctx.period || ""}
              typenames={["KPI", "Initiative"]}
              title={t("overview.Perspective")}
              onSelect={(event) => {
                if (event.category === "KPI") {
                  setShowKPIModal(event);
                }
                if (event.category === "Initiative") {
                  setShowInitiatives(event);
                }
              }}
            />
          </>
        ) : null}
      </PageContainer>
    </Page>
  );
};

export default Overview;
